import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, ChefHat, CheckCircle2, Flame, ArrowRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { EmptyState } from "@/components/Primitives";
import { StaggerRoot, StaggerItem } from "@/components/motion";
import { useOperationalSync } from "@/hooks/useOperationalSync";

const STATUSES = ["sent", "preparing", "ready"];

function timeAgo(iso, lang) {
  if (!iso) return "-";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const m = Math.max(0, Math.floor(diffMs / 60000));
  if (m < 1) return lang === "pashto" ? "همدا اوس" : "همین حالا";
  if (m < 60) return `${m} ${lang === "pashto" ? "دقیقې" : "دقیقه"}`;
  const h = Math.floor(m / 60);
  return `${h} ${lang === "pashto" ? "ساعته" : "ساعت"}`;
}

export default function Kitchen() {
  const { t, lang, tr } = useI18n();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [tables, setTables] = useState({});
  const [pulseId, setPulseId] = useState(null);
  const [initialLoad, setInitialLoad] = useState(false);

  const load = useCallback(async () => {
    const [r1, r2] = await Promise.all([
      api.get("/orders", { params: { status: STATUSES.join(","), limit: 300 } }),
      api.get("/tables", { params: { limit: 500 } }),
    ]);
    setOrders(r1.data);
    setTables(r2.data.reduce((m, t) => ({ ...m, [t.id]: t }), {}));
    setInitialLoad(true);
  }, []);

  useOperationalSync(load, { pollMs: 5000 });

  const grouped = STATUSES.reduce((acc, s) => ({ ...acc, [s]: [] }), {});
  orders.forEach((o) => grouped[o.status]?.push(o));
  const visible = filter === "all" ? orders : grouped[filter];

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      setPulseId(id);
      window.setTimeout(() => setPulseId(null), 500);
      toast.success(t("success"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <StaggerRoot className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("kitchen")}</h1>
          <p className="text-sm text-gray-500 mt-1">{orders.length} {lang === "pashto" ? "فرمایشونه" : "سفارش"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterBtn cur={filter} val="all" onClick={setFilter} label={t("allCategories")} count={orders.length} />
          <FilterBtn cur={filter} val="sent" onClick={setFilter} label={t("new")} count={grouped.sent.length} color="#0EA5E9" />
          <FilterBtn cur={filter} val="preparing" onClick={setFilter} label={t("preparing")} count={grouped.preparing.length} color="#F59E0B" />
          <FilterBtn cur={filter} val="ready" onClick={setFilter} label={t("ready")} count={grouped.ready.length} color="#16A34A" />
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyState icon={ChefHat} title={t("noActiveOrders")} />
      ) : (
        <StaggerRoot className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" delay={0.04}>
          {visible.map((o, idx) => {
            const elapsed = Math.max(0, Math.floor((Date.now() - new Date(o.created_at).getTime()) / 60000));
            const delayed = elapsed > 20 && o.status !== "ready";
            const borderColor =
              o.status === "ready" ? "#16A34A" :
              o.status === "preparing" ? "#F59E0B" :
              "#0EA5E9";
            const tbl = tables[o.table_id];
            return (
              <StaggerItem key={o.id} index={initialLoad ? idx : 0}>
              <div
                data-testid={`kds-card-${o.order_number}`}
                className={`rp-card p-4 flex flex-col gap-3 transition-transform ${pulseId === o.id ? "rp-btn-success" : ""}`}
                style={{ borderRightWidth: 4, borderRightColor: delayed ? "#DC2626" : borderColor }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-gray-500">#{o.order_number}</div>
                    <div className="text-2xl font-bold font-heading text-gray-900 mt-0.5">
                      {tbl ? `${t("table")} ${tbl.number}` : t(o.order_type === "takeaway" ? "takeaway" : "delivery")}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{o.waiter_name}</div>
                  </div>
                  <div className="text-left">
                    <div className={`text-sm font-bold tabular ${delayed ? "text-rose-600 pulse-warn rounded-full px-2 py-0.5" : "text-gray-700"}`}>
                      <Clock className="w-3.5 h-3.5 inline-block ml-1" />
                      {timeAgo(o.created_at, lang)}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {o.items.map((it) => (
                    <div key={it.id} className="flex items-start justify-between gap-2 border-t border-gray-100 pt-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-base">
                          {lang === "pashto" ? it.name_snapshot_pashto : it.name_snapshot_dari}
                        </div>
                        {it.notes && (
                          <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-md mt-1 inline-block">
                            {it.notes}
                          </div>
                        )}
                      </div>
                      <div className="text-xl font-bold tabular text-[#071A33]">×{it.quantity}</div>
                    </div>
                  ))}
                </div>

                <div className="pt-2 border-t border-gray-100">
                  {o.status === "sent" && (
                    <button
                      onClick={() => updateStatus(o.id, "preparing")}
                      className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold inline-flex items-center justify-center gap-2 active:scale-95"
                      data-testid={`kds-start-${o.order_number}`}
                    >
                      <Flame className="w-4 h-4" /> {t("startPreparing")}
                    </button>
                  )}
                  {o.status === "preparing" && (
                    <button
                      onClick={() => updateStatus(o.id, "ready")}
                      className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold inline-flex items-center justify-center gap-2 active:scale-95"
                      data-testid={`kds-ready-${o.order_number}`}
                    >
                      <CheckCircle2 className="w-4 h-4" /> {t("markReady")}
                    </button>
                  )}
                  {o.status === "ready" && (
                    <button
                      onClick={() => updateStatus(o.id, "served")}
                      className="w-full py-3 rounded-xl bg-[#071A33] hover:bg-[#7F1020] text-white text-sm font-bold inline-flex items-center justify-center gap-2 active:scale-95"
                    >
                      <ArrowRight className="w-4 h-4" /> {t("markServed")}
                    </button>
                  )}
                </div>
              </div>
              </StaggerItem>
            );
          })}
        </StaggerRoot>
      )}
    </StaggerRoot>
  );
}

function FilterBtn({ cur, val, onClick, label, count, color }) {
  const active = cur === val;
  return (
    <button
      onClick={() => onClick(val)}
      className={`px-4 py-2 rounded-xl text-sm font-medium border inline-flex items-center gap-2 ${active ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}
    >
      {color && <span className="w-2 h-2 rounded-full" style={{ background: color }} />}
      {label}
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-gray-100"}`}>{count}</span>
    </button>
  );
}
